import { useEffect, useState } from 'react';
import NoIndex from 'components/global/NoIndex';
import { withRouter } from 'react-router-dom';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';

import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import axios from 'axios';
import { injectIntl, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import LocaleSelect from 'components/global/LocaleSelect';
import { API_URL } from '../../../environment';
import {
  required,
  email,
  passwordLength,
  matchingPassword,
} from '../../global/validation';

const theme = createTheme();

const OnBoardingClinic = props => {
  const [clinicToken, setClinicToken] = useState('');
  const [apiError, setApiError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('clinic_token');

    if (!token) {
      props.history.push('/signin');
    } else {
      setClinicToken(token);
    }
    localStorage.removeItem('token'); // delete any existing token
  }, []);

  const handleSubmit = async event => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    if (!validateForm(data)) {
      return;
    }

    const locale = localStorage.getItem('locale') || 'en';
    const user = {
      first_name: data.get('firstName'),
      last_name: data.get('lastName'),
      email: data.get('email'),
      password: data.get('password'),
      locale,
    };
    const clinic = {
      clinic_token: clinicToken,
    };
    try {
      const registerRes = await axios.post(`${API_URL}/clinic_admin/users`, {
        user,
        clinic,
      });
      const token = registerRes.data.access_token;
      localStorage.setItem('token', token);
      props.history.push('/dashboard_clinic');
    } catch (error) {
      const errorData = error.response.data;
      let combinedErrorMessage = '';

      if (errorData.errors) {
        combinedErrorMessage = errorData.errors
          .map(err => `${err.source.pointer}: ${err.detail}`)
          .join('\n');
        setApiError(combinedErrorMessage);
      } else {
        let errorMessage = `${props.intl.formatMessage({
          defaultMessage: 'Something went wrong',
        })}: `;
        errorMessage += error?.response?.data?.error || '';
        setApiError(errorMessage);
      }
    }
  };

  const validateForm = data => {
    const errors = {};
    if (required(data.get('firstName'))) {
      errors.firstName = required(data.get('firstName'));
    }

    if (required(data.get('lastName'))) {
      errors.lastName = required(data.get('lastName'));
    }

    if (required(data.get('email'))) {
      errors.email = required(data.get('email'));
    } else if (email(data.get('email'))) {
      errors.email = email(data.get('email'));
    }

    if (required(data.get('password'))) {
      errors.password = required(data.get('password'));
    } else if (passwordLength(data.get('password'))) {
      errors.password = passwordLength(data.get('password'));
    }

    if (required(data.get('confirmPassword'))) {
      errors.confirmPassword = required(data.get('confirmPassword'));
    } else if (matchingPassword(data.get('confirmPassword'))) {
      errors.confirmPassword = matchingPassword(data.get('confirmPassword'));
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <NoIndex />
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <div style={{ marginBottom: '100px' }}>
            <AppBar position="absolute">
              <Toolbar
                sx={t => ({
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: t.spacing(0, 2),
                })}
              >
                <LocaleSelect variant="light" />
              </Toolbar>
            </AppBar>
          </div>
          <div
            style={{
              marginTop: theme.spacing(8),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography component="h1" variant="h5">
              <FormattedMessage defaultMessage="Clinic Administrator Sign up" />
            </Typography>
            <form
              style={{
                width: '100%',
                marginTop: theme.spacing(3),
              }}
              onSubmit={handleSubmit}
              onChange={() => setApiError('')}
            >
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    autoComplete="first-name"
                    name="firstName"
                    fullWidth
                    id="firstName"
                    label={<FormattedMessage defaultMessage="First Name" />}
                    autoFocus
                  />
                  {validationErrors.firstName && (
                    <Typography
                      sx={t => ({
                        color: t.palette.error.main,
                        marginTop: t.spacing(1),
                      })}
                    >
                      {validationErrors.firstName}
                    </Typography>
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    id="lastName"
                    label={<FormattedMessage defaultMessage="Last Name" />}
                    name="lastName"
                    autoComplete="last-name"
                  />
                  {validationErrors.lastName && (
                    <Typography
                      sx={t => ({
                        color: t.palette.error.main,
                        marginTop: t.spacing(1),
                      })}
                    >
                      {validationErrors.lastName}
                    </Typography>
                  )}
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    id="email"
                    label={<FormattedMessage defaultMessage="Email" />}
                    name="email"
                    autoComplete="email"
                  />
                  {validationErrors.email && (
                    <Typography
                      sx={t => ({
                        color: t.palette.error.main,
                        marginTop: t.spacing(1),
                      })}
                    >
                      {validationErrors.email}
                    </Typography>
                  )}
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    name="password"
                    label={<FormattedMessage defaultMessage="Password" />}
                    type="password"
                    id="password"
                  />
                  {validationErrors.password && (
                    <Typography
                      sx={t => ({
                        color: t.palette.error.main,
                        marginTop: t.spacing(1),
                      })}
                    >
                      {validationErrors.password}
                    </Typography>
                  )}
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    name="confirmPassword"
                    label={
                      <FormattedMessage defaultMessage="Confirm Password" />
                    }
                    type="password"
                    id="confirmPassword"
                  />
                  {validationErrors.confirmPassword && (
                    <Typography
                      sx={t => ({
                        color: t.palette.error.main,
                        marginTop: t.spacing(1),
                      })}
                    >
                      {validationErrors.confirmPassword}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                sx={t => ({
                  margin: t.spacing(3, 0, 2),
                })}
                disabled={!!apiError}
              >
                <FormattedMessage defaultMessage="Sign Up" />
              </Button>
            </form>
            {apiError && (
              <Typography
                sx={t => ({
                  color: t.palette.error.main,
                  marginTop: t.spacing(1),
                })}
              >
                {apiError}
              </Typography>
            )}
          </div>
        </Container>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

OnBoardingClinic.propTypes = {
  history: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
};

export default withRouter(injectIntl(OnBoardingClinic));
